import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { CompanyAddressInput } from "./use-companies";

const KEY = "leads";

export function useLeadList(options?: {
	status?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	page?: number;
	limit?: number;
	enabled?: boolean;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [
			KEY,
			"list",
			{
				status: options?.status,
				search: options?.search,
				createdAtFrom: options?.createdAtFrom,
				createdAtTo: options?.createdAtTo,
				page,
				limit,
			},
		],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			return api.get<{ items: Lead[]; total: number }>(`/api/leads?${params.toString()}`);
		},
		enabled: options?.enabled !== false,
	});
}

export function useLeadDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Lead>(`/api/leads/${id}`),
		enabled: !!id,
	});
}

export function useCreateLead() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateLeadInput) => api.post<Lead>("/api/leads", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateLead() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateLeadInput> }) =>
			api.patch<Lead>(`/api/leads/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteLead() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/leads/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useConvertLead() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data?: ConvertLeadInput }) =>
			api.post<{ company: { id: string }; person: { id: string } }>(
				`/api/leads/${id}/convert`,
				data ?? {},
			),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

interface LeadAddress {
	id: string;
	street: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string | null;
}

export interface Lead {
	id: string;
	owner: { id: string; firstName: string | null; lastName: string | null } | null;
	firstName: string;
	lastName: string;
	email: string | null;
	phone: string | null;
	companyName: string | null;
	tradeName: string | null;
	taxNumber: string | null;
	taxOffice: string | null;
	fax: string | null;
	website: string | null;
	industry: string | null;
	title: string | null;
	source: string | null;
	campaign: string | null;
	status: "new" | "converted";
	notes: string | null;
	address?: LeadAddress | null;
	convertedCompanyId: string | null;
	convertedPersonId: string | null;
	convertedAt: string | null;
	assignedTo: string | null;
	createdAt: string;
	updatedAt: string;
}

interface CreateLeadInput {
	firstName: string;
	lastName: string;
	email?: string;
	phone?: string;
	companyName?: string;
	title?: string;
	source?: string;
	campaign?: string;
	notes?: string;
	assignedTo?: string;
	address?: CompanyAddressInput | undefined;
}

export interface ConvertLeadInput {
	legalName?: string | undefined;
	tradeName?: string | undefined;
	status?: "lead" | "customer" | "vendor" | "inactive" | undefined;
	sapSeries?: number | undefined;
	sapGroupCode?: number | undefined;
	currency?: string | undefined;
	taxNumber?: string | undefined;
	taxOffice?: string | undefined;
	email?: string | undefined;
	phone?: string | undefined;
	fax?: string | undefined;
	website?: string | undefined;
	industry?: string | undefined;
	notes?: string | undefined;
	ownerId?: string | undefined;
	address?: CompanyAddressInput | undefined;
}
